/**
 * Password Validation Utilities
 * @implements FS-001.2.2 - Password Authentication (Backup)
 *
 * Client-side validation rules for password complexity:
 * - Minimum 8 characters
 * - At least one uppercase letter
 * - At least one lowercase letter
 * - At least one digit
 */

/**
 * Password validation error types
 */
export interface PasswordValidationResult {
  isValid: boolean;
  errors: PasswordValidationError[];
}

export interface PasswordValidationError {
  code: PasswordErrorCode;
  message: string;
}

export type PasswordErrorCode =
  | 'TOO_SHORT'
  | 'NO_UPPERCASE'
  | 'NO_LOWERCASE'
  | 'NO_DIGIT';

/**
 * Password requirements for display
 */
export interface PasswordRequirement {
  code: PasswordErrorCode;
  label: string;
  test: (password: string) => boolean;
}

/**
 * Password requirements with their validation functions
 * @implements FS-001.2.2 - Password Validation Rules
 */
export const PASSWORD_REQUIREMENTS: PasswordRequirement[] = [
  {
    code: 'TOO_SHORT',
    label: 'At least 8 characters',
    test: (password: string) => password.length >= 8,
  },
  {
    code: 'NO_UPPERCASE',
    label: 'At least one uppercase letter',
    test: (password: string) => /[A-Z]/.test(password),
  },
  {
    code: 'NO_LOWERCASE',
    label: 'At least one lowercase letter',
    test: (password: string) => /[a-z]/.test(password),
  },
  {
    code: 'NO_DIGIT',
    label: 'At least one digit',
    test: (password: string) => /\d/.test(password),
  },
];

/**
 * Validate a password against all requirements
 * @implements FS-001.2.2 - Password Authentication (Backup)
 *
 * @param password - The password to validate
 * @returns Validation result with any errors
 */
export function validatePassword(password: string): PasswordValidationResult {
  const errors: PasswordValidationError[] = [];

  for (const requirement of PASSWORD_REQUIREMENTS) {
    if (!requirement.test(password)) {
      errors.push({
        code: requirement.code,
        message: requirement.label,
      });
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Check if a specific requirement is met
 * @param password - The password to check
 * @param code - The requirement code to check
 * @returns Whether the requirement is met
 */
export function checkRequirement(
  password: string,
  code: PasswordErrorCode,
): boolean {
  const requirement = PASSWORD_REQUIREMENTS.find((r) => r.code === code);
  return requirement ? requirement.test(password) : false;
}

/**
 * Validate that two passwords match
 * @param password - The password
 * @param confirmPassword - The confirmation password
 * @returns Whether the passwords match
 */
export function passwordsMatch(
  password: string,
  confirmPassword: string,
): boolean {
  return password === confirmPassword;
}
